// evaluateAdults.js — Avaliação completa para adultos
// Consolida avaliação de kits (PurissimaSolutions) + nutracêuticos (SolucoesNutraceuticas)

// Funções auxiliares locais para evitar problemas de import
function safeGetRaw(answers, id) {
  const a = answers?.[id];
  if (a == null) return undefined;
  if (typeof a === "object" && "answer" in a) return a.answer;
  return a;
}

function normalizeAnswerArray(raw) {
  if (Array.isArray(raw))
    return raw.map((v) => Number(v)).filter(Number.isFinite);
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.items)) {
      return raw.items.map((v) => Number(v)).filter(Number.isFinite);
    }
    if (Array.isArray(raw.answer)) {
      return raw.answer.map((v) => Number(v)).filter(Number.isFinite);
    }
  }
  const single = Number(raw);
  return Number.isFinite(single) ? [single] : [];
}

function resolveGoalFromAnswers(answers) {
  const goalAnswer = Number(safeGetRaw(answers, 227));
  const ozempicAnswer = Number(safeGetRaw(answers, 471));

  if((parseInt(goalAnswer, 10) === 183) && (parseInt(ozempicAnswer, 10) !== 420) ){
    return "weight_loss";
  } else if (parseInt(goalAnswer, 10) === 184) {
    return "performance";
  }

  // Fluxo novo: pergunta 105 (check múltipla)
  // 180 = perda de peso, 181 = ganho muscular/performance
  // const motivationAnswers = normalizeAnswerArray(safeGetRaw(answers, 105));
  // if (motivationAnswers.includes(180)) return "weight_loss";
  // if (motivationAnswers.includes(181)) return "performance";

  return "";
}

function baseResult() {
  return {
    sku: null,
    scores: {},
    regenera_flags: { G1: 0, G2: 0, G3: 0, G4: 0 },
    preliminarySKU: null,
    boostedScores: null,
    reasons: [],
    alerts: [],
  };
}

const SPECIAL_SUBJECT = 45;
const SUBJECT_24 = 24;
const SUBJECT_DIGESTAO = 998; // 24 + sym_group 11
const SUBJECT_INTESTINAL = 999; // 24 + sym_group 12

/**
 * Determina o kit baseado em idade, sexo e objetivos
 */
function determineKit(answers) {
  const ageAnswer = safeGetRaw(answers, 253); // data de nascimento
  const genderAnswer = safeGetRaw(answers, 254); // sexo
  const pregnancyAnswer = safeGetRaw(answers, 472); // gravidez
  const ozempicAnswer = safeGetRaw(answers, 471); // uso de ozempic

  if (!ageAnswer || !genderAnswer) {
    return {
      kit: null,
      filters: null,
      alerts: ["Idade e sexo são obrigatórios para determinação do kit"],
    };
  }

  // Calcular idade
  const [day, month, year] = ageAnswer.split("/").map(Number);
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  if (
    today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  const ageGroup = age > 35 ? "over_35" : "under_35";
  const gender = parseInt(genderAnswer, 10) === 189 ? 1 : 2; // 189 = masculino, assumindo feminino = 190

  // Determinar objetivo
  let goal = "";
  if (
    parseInt(pregnancyAnswer, 10) === 418 ||
    parseInt(pregnancyAnswer, 10) === 537 ||
    parseInt(pregnancyAnswer, 10) === 542
  ) {
    goal = "pregnancy";
  } else if (parseInt(ozempicAnswer, 10) === 420) {
    goal = "weight_loss";
  } else {
    goal = resolveGoalFromAnswers(answers);
  }

  const filters = { gender, ageGroup, goal };

  // Determinar SKU do kit baseado nos filtros
  // Esta lógica deveria idealmente consultar a API, mas vou criar uma heurística básica
  let kitSku = null;
  if (goal === "pregnancy") {
    kitSku = "PregnancyKit";
  } else if (goal === "weight_loss") {
    kitSku = gender === 1 ? "WeightLossMale" : "WeightLossFemale";
  } else if (goal === "performance") {
    kitSku = gender === 1 ? "PerformanceMale" : "PerformanceFemale";
  } else {
    // Kit padrão baseado em idade e sexo
    if (ageGroup === "over_35") {
      kitSku = gender === 1 ? "VitalityMaleOver35" : "VitalityFemaleOver35";
    } else {
      kitSku = gender === 1 ? "VitalityMaleUnder35" : "VitalityFemaleUnder35";
    }
  }

  return { kit: kitSku, filters, alerts: [] };
}

/**
 * Soma respostas por subject (sym_for === 2) exceto o SPECIAL_SUBJECT
 */
function groupAnswersBySubject(answers) {
  const grouped = {};
  Object.entries(answers || {}).forEach(([_, response]) => {
    if (!response || typeof response !== "object") return;
    if (response.subject === SPECIAL_SUBJECT) return;
    if (response.sym_for !== 2) return;

    let subject = response.subject;
    if (subject === SUBJECT_24) {
      if (response.sym_group === 11) subject = SUBJECT_DIGESTAO;
      else if (response.sym_group === 12) subject = SUBJECT_INTESTINAL;
      else return; // ignora outros grupos do 24
    }

    const raw =
      typeof response.answer === "object"
        ? response.answer?.answer
        : response.answer;
    let val = parseFloat(raw ?? 0);
    // Normalizar para 0-10 se slider tiver max diferente de 10
    let sliderMax = 10;
    if (response.type === "range") {
      sliderMax = response.max ?? 10;
      if (sliderMax !== 10 && sliderMax > 0) {
        val = Math.round((val / sliderMax) * 10);
      }
    }
    if (Number.isFinite(val) && subject) {
      // A API envia reverse como booleano (true/null), nunca como 1/"1".
      const isReverse =
        response.reverse === true ||
        response.reverse === 1 ||
        response.reverse === "1";
      if (response.type === "range" && !isReverse) {
        val = 10 - val;
      }
      grouped[subject] = (grouped[subject] || 0) + val;
    }
  });
  return grouped;
}

/**
 * Extrai sintomas do subject 45 individualmente e múltiplos selecionados
 */
function extractSubject45Details(answers) {
  const symptoms = [];
  const multiSet = new Set();

  Object.entries(answers || {}).forEach(([key, response]) => {
    if (!response || typeof response !== "object") return;
    if (response.subject !== SPECIAL_SUBJECT) return;

    const numericIdxKeys = Object.keys(response).filter((k) => /^\d+$/.test(k));
    if (numericIdxKeys.length > 0 && response.answer === undefined) {
      numericIdxKeys.forEach((idxKey) => {
        const val = Number(response[idxKey]);
        if (Number.isFinite(val)) multiSet.add(val);
      });
      return;
    }

    const symId = Number(key);
    let raw = response.answer;
    if (typeof raw === "object") raw = raw?.answer;
    const value = parseFloat(raw ?? 0);

    if (Number.isFinite(symId) && value > 0) {
      symptoms.push({
        sym_id: symId,
        value,
        sym_group: response.sym_group ?? null,
      });
    }
  });

  symptoms.sort((a, b) => a.sym_id - b.sym_id);
  const multi = Array.from(multiSet).sort((a, b) => a - b);
  return { symptoms, multi };
}

/**
 * Determina fórmulas nutracêuticas baseado nos sintomas
 */
function determineNutraceuticals(answers) {
  // Verificar se está grávida
  const pregnancyAnswer = safeGetRaw(answers, 472);
  if (
    parseInt(pregnancyAnswer, 10) === 418 ||
    parseInt(pregnancyAnswer, 10) === 537 ||
    parseInt(pregnancyAnswer, 10) === 542
  ) {
    // Mesmo grávida, preserva o objetivo para que o produto de emagrecimento
    // possa ser exibido separadamente (sem os nutracêuticos baseados em sintomas).
    const ozAns = safeGetRaw(answers, 471);
    const pregnancyGoal =
      parseInt(ozAns, 10) === 420 ? "weight_loss" : resolveGoalFromAnswers(answers);
    return {
      formulas: [],
      filters: {
        pregnancy: 1,
        ...(pregnancyGoal ? { goal: pregnancyGoal } : {}),
      },
      alerts: [
        "Grávidas não devem usar fórmulas nutracêuticas sem orientação médica",
      ],
    };
  }

  const groupedSubjects = groupAnswersBySubject(answers);
  const { symptoms, multi } = extractSubject45Details(answers);

  // Determinar objetivo para fórmulas baseado em respostas históricas e novas
  let goal = resolveGoalFromAnswers(answers);

  // Agrupar sintomas do subject 45 por sym_group
  const subject45SymptomsRaw = symptoms.filter((s) => s.value > 0);
  const groupMap = subject45SymptomsRaw.reduce((acc, s) => {
    const key = s.sym_group ?? `__ungrouped_${s.sym_id}`;
    if (!acc[key])
      acc[key] = { sym_group: s.sym_group ?? null, total: 0, symptoms: [] };
    acc[key].total += s.value;
    acc[key].symptoms.push({ sym_id: s.sym_id, value: s.value });
    return acc;
  }, {});

  const subject45Groups = Object.values(groupMap)
    .map((g) => ({
      sym_group: g.sym_group,
      total: g.total,
      symptoms: g.symptoms.sort((a, b) => a.sym_id - b.sym_id),
    }))
    .sort((a, b) => b.total - a.total);

  // Determinar fórmulas recomendadas baseado nos scores mais altos
  const formulaScores = [];

  // Priorizar os 3 subjects com maiores scores
  const topSubjects = Object.entries(groupedSubjects)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .filter(([_, score]) => score > 5); // só considera se score significativo

  topSubjects.forEach(([subjectId, score]) => {
    formulaScores.push({
      type: "subject",
      id: parseInt(subjectId),
      score,
      priority: score,
    });
  });

  // Adicionar fórmulas baseadas no subject 45 (sintomas específicos)
  subject45Groups.slice(0, 2).forEach((group, index) => {
    if (group.total > 3) {
      // threshold mínimo
      formulaScores.push({
        type: "symptom_group",
        id: group.sym_group,
        score: group.total,
        priority: group.total - index * 0.5, // pequeno penalty para ordem
      });
    }
  });

  // Ordenar por prioridade
  formulaScores.sort((a, b) => b.priority - a.priority);

  const filters = {
    ...groupedSubjects,
    subjects: groupedSubjects,
    subject45Symptoms: symptoms,
    subject45Multi: multi,
    goal,
    subject45: { symptoms: subject45Groups, multi },
  };

  return {
    formulas: formulaScores.slice(0, 3), // máximo 3 fórmulas
    filters,
    alerts: [],
  };
}

/**
 * Função principal de avaliação para adultos
 */
export default function evaluateAdults({ answers = {}, questions = [] } = {}) {
  const result = baseResult();

  // Determinar kit
  const { kit, filters: kitFilters, alerts: kitAlerts } = determineKit(answers);

  // Determinar nutracêuticos
  const {
    formulas,
    filters: formulaFilters,
    alerts: formulaAlerts,
  } = determineNutraceuticals(answers);

  // Consolidar resultado
  result.sku = kit;
  result.kit = {
    sku: kit,
    filters: kitFilters,
  };

  result.nutraceuticals = {
    formulas,
    filters: formulaFilters,
  };

  result.alerts = [...kitAlerts, ...formulaAlerts];

  // Calcular scores baseado nos subjects para compatibilidade
  if (formulaFilters && formulaFilters.subjects) {
    const subjects = formulaFilters.subjects;
    result.scores = {
      Digestive: Math.round((subjects[SUBJECT_DIGESTAO] || 0) * 10), // normalizar para 0-100
      Intestinal: Math.round((subjects[SUBJECT_INTESTINAL] || 0) * 10),
      // Adicionar outros subjects principais
      Stress: Math.round((subjects[1] || 0) * 10), // assumindo subject 1 = stress
      Sleep: Math.round((subjects[2] || 0) * 10), // assumindo subject 2 = sono
      Energy: Math.round((subjects[3] || 0) * 10), // assumindo subject 3 = energia
    };
  }

  // Adicionar reasons baseado nos scores mais altos
  const reasons = [];
  if (result.scores) {
    Object.entries(result.scores).forEach(([area, score]) => {
      if (score >= 70) {
        reasons.push({
          area,
          score,
          weight: score,
          label: `Score alto em ${area}: ${score}/100`,
        });
      }
    });
  }

  result.reasons = reasons.sort((a, b) => b.score - a.score).slice(0, 5);

  return result;
}

/**
 * Função auxiliar para obter apenas os filtros de kit (compatibilidade com hook atual)
 */
export function getKitFilters(answers) {
  const { filters } = determineKit(answers);
  return filters;
}

/**
 * Função auxiliar para obter apenas os filtros de fórmulas (compatibilidade com hook atual)
 */
export function getFormulaFilters(answers) {
  const { filters } = determineNutraceuticals(answers);
  return filters;
}
